import React, { createContext, useContext, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import NavigationService from './navigation.service'

// Create a navigation context to prevent navigation state from being lost
export const NavigationContext = createContext<{
  navigateTo: (path: string) => void
}>({
  navigateTo: () => {},
})

// Hook to use the navigation context
export const useNavigation = () => useContext(NavigationContext)

// Navigation provider component
export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()

  // Set up the navigation service with React Router
  useEffect(() => {
    NavigationService.getInstance().setNavigate(path => {
      navigate(path)
    })
  }, [navigate])

  // Check for service worker navigation hash parameter
  useEffect(() => {
    // Look for our special hash parameter
    const hash = window.location.hash
    const match = hash.match(/#sw-navigate=([^&]*)/)

    if (match && match[1]) {
      try {
        const targetPath = decodeURIComponent(match[1])
        console.log('Navigating via hash parameter to:', targetPath)

        // Clear the hash
        window.history.replaceState(
          null,
          document.title,
          window.location.pathname + window.location.search
        )

        // Navigate to the target path
        setTimeout(() => {
          navigate(targetPath)
        }, 100)
      } catch (err) {
        console.error('Error processing hash navigation:', err)
      }
    }
  }, [navigate])

  // Set up service worker message listener
  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service worker is not supported in this browser')
      return
    }

    // Create a listener for messages from the service worker
    const messageListener = (event: MessageEvent) => {
      // Ensure the message is coming from our service worker and has the right format
      if (event.data && event.data.type === 'NAVIGATE') {
        const targetPath = event.data.url

        if (targetPath) {
          console.log('Navigating to:', targetPath)
          navigate(targetPath)
        }
      }
    }

    // Add the listener
    navigator.serviceWorker.addEventListener('message', messageListener)

    // Notify service worker that client is ready
    navigator.serviceWorker.ready
      .then(registration => {
        if (registration.active) {
          registration.active.postMessage({
            type: 'CLIENT_READY',
            timestamp: Date.now(),
          })
        }
      })
      .catch(err => {
        console.error('Error with service worker registration:', err)
      })

    // Clean up on component unmount
    return () => {
      navigator.serviceWorker.removeEventListener('message', messageListener)
    }
  }, [navigate])

  // Provide the navigation context
  const navigateTo = (path: string) => {
    navigate(path)
  }

  return <NavigationContext.Provider value={{ navigateTo }}>{children}</NavigationContext.Provider>
}
